/**
 * ReleaseForFabrication — PM human gate (CR-2026-SFM-UPLOAD-ROUTING Phase 4).
 *
 * Lists pieces whose routing is DRAFT (auto-drafted on drawing upload, still
 * PENDING_ISSUE so the shop floor is blocked by FAB-V01). The PM reviews the
 * ABOM (assembly → members) against the linked drawing and clicks Release —
 * which flips pieces to ISSUED/RELEASED and hands them to the stage engine.
 *
 * Permission-gated server-side via "Release for Fabrication" (PM/Ops/Admin/CEO).
 */
import React, { useState, useEffect, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import {
  Box, Container, Card, Typography, Stack, Button, Select, MenuItem,
  FormControl, Table, TableHead, TableBody, TableRow, TableCell,
  Checkbox, Chip, Collapse, IconButton, Alert, LinearProgress, TextField, FormControlLabel,
} from "@mui/material";
import {
  PlayArrowRounded, ExpandMore, ExpandLess, EngineeringRounded,
} from "@mui/icons-material";
import { getJobs } from "../../feature/jobSlice";
import { listDraftRouting, releaseForFabrication } from "../../utils/routingApi";
import { showSuccessToast, showErrorToast } from "../../utils/Toast";

export default function ReleaseForFabrication() {
  const dispatch = useDispatch();
  const jobs = useSelector((s) => s.jobSlice?.jobs || []);

  const [jobId, setJobId] = useState("");
  const [assemblies, setAssemblies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [selected, setSelected] = useState({});   // { pieceId: true }
  const [expanded, setExpanded] = useState({});    // { assemblyId: true }
  const [note, setNote] = useState("");
  const [approvalBasis, setApprovalBasis] = useState("customer_approved");
  const [contractRef, setContractRef] = useState("");
  const [riskAck, setRiskAck] = useState(false);
  const atRisk = approvalBasis === "at_risk";

  useEffect(() => { dispatch(getJobs()); }, [dispatch]);

  const load = useCallback(async (jid) => {
    if (!jid) return;
    setLoading(true);
    try {
      const data = await listDraftRouting(jid);
      setAssemblies(data?.assemblies || []);
      setSelected({});
    } catch (e) {
      showErrorToast(e?.response?.data?.message || "Failed to load draft routing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (jobId) load(jobId); }, [jobId, load]);

  const draftCount = assemblies.reduce((n, a) => n + 1 + (a.members?.length || 0), 0);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]).map(Number);

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const toggleAssembly = (a) => {
    const ids = [a.id, ...(a.members || []).map((m) => m.id)];
    const allOn = ids.every((id) => selected[id]);
    setSelected((p) => { const n = { ...p }; ids.forEach((id) => { n[id] = !allOn; }); return n; });
  };

  const doRelease = async (scope) => {
    if (atRisk && (!contractRef.trim() || !riskAck)) {
      showErrorToast("At-risk release requires a contract reference and the 'I accept fabrication risk' acknowledgment.");
      return;
    }
    setReleasing(true);
    try {
      if (scope === "job") {
        await releaseForFabrication({ scope: "job", jobId, note, approvalBasis, contractReference: contractRef, riskAcknowledged: riskAck });
      } else {
        // per-piece selected — release each (per-piece atomic per spec)
        for (const pieceId of selectedIds) {
          await releaseForFabrication({ scope: "piece", pieceId, note, approvalBasis, contractReference: contractRef, riskAcknowledged: riskAck });
        }
      }
      showSuccessToast("Released for fabrication.");
      await load(jobId);
      setNote("");
      setContractRef(""); setRiskAck(false); setApprovalBasis("customer_approved");
    } catch (e) {
      showErrorToast(e?.response?.data?.message || "Release failed");
    } finally {
      setReleasing(false);
    }
  };

  return (
    <>
      <Helmet><title>Drawing Package Release | SFM</title></Helmet>
      <Container>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <EngineeringRounded color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Drawing Package Release</Typography>
        </Stack>
        <PageGuide
          why="Make sure pieces are cleared — customer-approved drawing and a passed final check — before they go into the shop."
          who="Supervisors and QC, clearing pieces before they enter the shop."
          what="The gate that releases pieces, sequences, or whole jobs for fabrication."
          how="Pick the job and pieces, confirm the approval basis, and release. At-risk releases need a contract reference and sign-off."
          when="Once detailing is customer-approved and the drawing's final check is accepted."
          standard="AISC 303 §1.7.0"
        />

        <Card sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <Select value={jobId} onChange={(e) => setJobId(e.target.value)} displayEmpty>
                <MenuItem value="" disabled><em>Select Job</em></MenuItem>
                {jobs.map((j) => <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Chip label={`${draftCount} draft piece${draftCount !== 1 ? "s" : ""}`} color="warning" variant="outlined" />
            <Box sx={{ flex: 1 }} />
            <TextField size="small" label="Release note (optional)" value={note}
              onChange={(e) => setNote(e.target.value)} sx={{ minWidth: 220 }} />
            <Button variant="outlined" disabled={!selectedIds.length || releasing}
              startIcon={<PlayArrowRounded />} onClick={() => doRelease("selected")}>
              Release Selected ({selectedIds.length})
            </Button>
            <Button variant="contained" disabled={!draftCount || releasing}
              startIcon={<PlayArrowRounded />} onClick={() => doRelease("job")}>
              Release Whole Job
            </Button>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <Select value={approvalBasis} onChange={(e) => setApprovalBasis(e.target.value)}>
                <MenuItem value="customer_approved">Customer / EOR approved</MenuItem>
                <MenuItem value="at_risk">Release at fabricator&apos;s risk</MenuItem>
              </Select>
            </FormControl>
            {atRisk && (
              <>
                <TextField size="small" label="Contract reference (required)" value={contractRef}
                  onChange={(e) => setContractRef(e.target.value)} sx={{ minWidth: 280 }} required />
                <FormControlLabel control={<Checkbox checked={riskAck} onChange={(e) => setRiskAck(e.target.checked)} />}
                  label="I accept fabrication risk" />
              </>
            )}
          </Stack>
          {atRisk && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              At-risk release proceeds <strong>without</strong> customer/EOR approval (AISC 303 §1.7).
              Restricted to Operations Manager / CEO and recorded as an explicit risk-acceptance in the audit trail.
              Enter the governing contract clause that permits fabrication at risk.
            </Alert>
          )}
          {(loading || releasing) && <LinearProgress sx={{ mb: 2 }} />}

          {!loading && jobId && draftCount === 0 && (
            <Alert severity="info">No drafted routing awaiting release for this job. Upload steel-fab drawings to draft routing.</Alert>
          )}

          {draftCount > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell width={40} />
                  <TableCell sx={{ fontWeight: 700 }}>Mark</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Profile</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assemblies.map((a) => (
                  <React.Fragment key={a.id}>
                    <TableRow hover sx={{ bgcolor: "action.hover" }}>
                      <TableCell>
                        {(a.members?.length || 0) > 0 && (
                          <IconButton size="small" onClick={() => toggleExpand(a.id)}>
                            {expanded[a.id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Checkbox size="small" checked={!!selected[a.id]} onChange={() => toggleAssembly(a)} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{a.name}</TableCell>
                      <TableCell>{a.section || "—"}</TableCell>
                      <TableCell>{a.grade || "—"}</TableCell>
                      <TableCell align="right">{a.quantity}</TableCell>
                      <TableCell><Chip size="small" label="DRAFT" color="warning" /></TableCell>
                    </TableRow>
                    {(a.members?.length || 0) > 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                          <Collapse in={!!expanded[a.id]} unmountOnExit>
                            <Table size="small">
                              <TableBody>
                                {a.members.map((m) => (
                                  <TableRow key={m.id}>
                                    <TableCell width={40} />
                                    <TableCell width={40}>
                                      <Checkbox size="small" checked={!!selected[m.id]} onChange={() => toggle(m.id)} />
                                    </TableCell>
                                    <TableCell sx={{ pl: 4 }}>{m.name}</TableCell>
                                    <TableCell>{m.section || "—"}</TableCell>
                                    <TableCell>{m.grade || "—"}</TableCell>
                                    <TableCell align="right">{m.quantity}</TableCell>
                                    <TableCell><Chip size="small" label="member" variant="outlined" /></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </Container>
    </>
  );
}
